import { STAGES, deriveFlow, type StageName, type StageState } from "../flow";
import type { DemoRunResult } from "../types";

/**
 * Live Decision Flow. All state comes from `deriveFlow` (src/flow.ts), which reads only the
 * backend response - this component renders, it does not decide.
 *
 * Stage KEYS stay in engineering English (they are test/data identifiers); what the visitor
 * reads is a friendly Korean label with the technical term kept small underneath, so a
 * middle-schooler follows the story and an engineer still sees the real pipeline names.
 */

const STAGE_LABELS: Record<StageName, { icon: string; ko: string }> = {
  Transaction: { icon: "💸", ko: "송금 도착" },
  "Feature Calculation": { icon: "🔎", ko: "단서 모으기" },
  Predictor: { icon: "🤖", ko: "위험 점수 매기기" },
  "Policy Engine": { icon: "⚖️", ko: "판정" },
  "Step-up Verification": { icon: "📱", ko: "본인 확인" },
};

const STATE_LABELS: Record<StageState, string> = {
  idle: "대기",
  running: "실행 중",
  done: "완료",
  skipped: "건너뜀",
  failed: "실패",
};

function stageClass(state: StageState): string {
  return `flow-stage flow-${state}`;
}

interface Props {
  result: DemoRunResult | null;
  running: boolean;
}

export function DecisionFlow({ result, running }: Props) {
  const flow = deriveFlow(result, running);

  return (
    <div className="decision-flow" data-testid="decision-flow">
      <div className="flow-track">
        {STAGES.map((stage, index) => (
          <div key={stage} className="flow-node">
            <div className={stageClass(flow.stages[stage])} data-stage={stage}>
              <span className="flow-label">
                <span className="flow-icon">{STAGE_LABELS[stage].icon}</span>{" "}
                {STAGE_LABELS[stage].ko}
              </span>
              <span className="flow-tech">{stage}</span>
              <span className="flow-state">{STATE_LABELS[flow.stages[stage]]}</span>
            </div>
            {index < STAGES.length - 1 && <span className="flow-arrow">→</span>}
          </div>
        ))}
        {/* The arrow and the terminal stack wrap as ONE unit: an arrow left dangling at a
            line end reads as clipped (user-reported). */}
        <div className="flow-node">
          <span className="flow-arrow">→</span>
          <div className="flow-terminals">
          <div className={`flow-terminal ${flow.terminal === "APPROVE" ? "active approve" : ""}`}>
            ✅ 통과 <span className="flow-tech-inline">APPROVE</span>
          </div>
          <div className={`flow-terminal ${flow.terminal === "REVIEW" ? "active review" : ""}`}>
            🧑‍💼 사람이 확인 <span className="flow-tech-inline">REVIEW</span>
          </div>
          {/* Labelled by what happened, not by the status code. An engineer still sees the
              code in the bank-side view; a visitor should not have to look one up. */}
          <div className={`flow-terminal ${flow.terminal === "ERROR" ? "active danger" : ""}`}>
            ⚠️ 처리 안 됨
          </div>
          </div>
        </div>
      </div>

      {flow.banner && <p className="flow-banner">{flow.banner}</p>}
      {flow.terminalNote && <p className="flow-note">{flow.terminalNote}</p>}
    </div>
  );
}
